import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

type PostRow = {
  id: string;
  account_id: string;
  provider: 'facebook' | 'tiktok';
  publish_id: string | null;
  upload_url: string | null;
  release_id: string | null;
  release_url: string | null;
  state: 'init' | 'uploading' | 'processing' | 'published' | 'failed';
  error_code: string | null;
  error_message: string | null;
  content: string | null;
  privacy: string | null;
  created_at: number;
  updated_at: number;
};

export default function HistoryPage() {
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/posts?limit=50`)
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
        } else {
          setPosts(data.posts || []);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const getStatusColor = (state: string) => {
    switch (state) {
      case 'published': return '#2e7d32'; // green
      case 'failed': return '#c62828'; // red
      default: return '#1565c0'; // blue
    }
  };

  const getProviderColor = (provider: string) => {
    return provider === 'facebook' ? '#1877F2' : '#010101';
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.75rem', fontWeight: 600 }}>Publish History</h1>

      {loading && <div style={{ color: '#666' }}>Loading history...</div>}
      
      {error && (
        <div style={{ color: '#c62828', padding: '1rem', backgroundColor: '#ffebee', borderRadius: '6px', marginBottom: '1rem' }}>
          Error: {error}
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <div style={{ color: '#666' }}>No publish history found.</div>
      )}

      {!loading && !error && posts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {posts.map(post => (
            <div key={post.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', backgroundColor: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ 
                    fontWeight: 600, 
                    color: '#fff', 
                    backgroundColor: getProviderColor(post.provider),
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    textTransform: 'capitalize'
                  }}>
                    {post.provider}
                  </span>
                  <span style={{ color: '#666', fontSize: '0.85rem' }}>
                    {new Date(post.created_at).toLocaleString()}
                  </span>
                </div>
                <span style={{ 
                  fontWeight: 600, 
                  color: getStatusColor(post.state),
                  textTransform: 'uppercase',
                  fontSize: '0.85rem'
                }}>
                  {post.state}
                </span>
              </div>
              
              {post.content && (
                <p style={{ margin: '0 0 0.75rem 0', color: '#333', fontSize: '0.95rem' }}>
                  {post.content.length > 100 ? `${post.content.substring(0, 100)}...` : post.content}
                </p>
              )}

              {post.state === 'failed' && post.error_message && (
                <pre style={{ margin: 0, padding: '0.75rem', backgroundColor: '#fff5f5', color: '#c62828', borderRadius: '4px', fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {post.error_message}
                </pre>
              )}

              {post.state === 'published' && post.release_id && (
                <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.5rem' }}>
                  Release ID: {post.release_id}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
